import { useRef, useState, type ChangeEvent } from 'react';
import * as XLSX from 'xlsx';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormHelperText from '@mui/material/FormHelperText';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ContentPasteOutlinedIcon from '@mui/icons-material/ContentPasteOutlined';
import type { FieldOption } from '../types';

export type BulkImportMode = 'text' | 'options' | 'singleOption';

interface BulkImportActionsProps {
  mode: BulkImportMode;
  options?: FieldOption[];
  currentValue: string | string[];
  onApply: (value: string | string[]) => void;
  excludedValues?: string[];
  buttonLabel?: string;
}

export interface BulkImportValueParams {
  mode: BulkImportMode;
  options?: FieldOption[];
  currentValue: string | string[];
  excludedValues?: string[];
  rawValues: string[];
}

export interface BulkImportValueResult {
  applied: boolean;
  value?: string | string[];
  message: string;
}

type SpreadsheetCellValue = string | number | boolean | Date | null | undefined;

interface SpreadsheetCell {
  v?: SpreadsheetCellValue;
  w?: string;
}

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function isScientificNotation(value: string): boolean {
  return /^[-+]?\d+(?:[\.,]\d+)?e[-+]?\d+$/i.test(value.trim());
}

function numberToPlainText(value: number): string {
  if (!Number.isFinite(value)) return '';

  const formatted = value.toLocaleString('en-US', {
    useGrouping: false,
    maximumFractionDigits: 20,
  });

  return formatted.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function scientificTextToPlainText(value: string): string {
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) ? numberToPlainText(parsed) : value;
}

function shouldUseFormattedNumericText(formatted: string, rawText: string): boolean {
  if (!formatted || isScientificNotation(formatted)) return false;

  if (/^\d+$/.test(formatted)) {
    return formatted.length >= rawText.length;
  }

  const formattedDigits = onlyDigits(formatted);
  const rawDigits = onlyDigits(rawText);

  return (
    formattedDigits.length >= 11 &&
    formattedDigits.length >= rawDigits.length &&
    /[/-]/.test(formatted)
  );
}

function spreadsheetCellToText(cell: SpreadsheetCell): string {
  const rawValue = cell.v;
  if (rawValue === null || rawValue === undefined) return '';

  if (typeof rawValue === 'number') {
    const rawText = numberToPlainText(rawValue);
    const formattedText = String(cell.w ?? '').trim();

    return shouldUseFormattedNumericText(formattedText, rawText)
      ? formattedText
      : rawText;
  }

  const value = rawValue instanceof Date ? rawValue.toISOString() : String(rawValue).trim();
  return isScientificNotation(value) ? scientificTextToPlainText(value) : value;
}

function splitValues(raw: string): string[] {
  return raw
    .split(/[\n\r\t,;|]+/)
    .map((value) => value.trim())
    .filter(Boolean);
}

export function parseBulkImportText(raw: string): string[] {
  return splitValues(raw);
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

function optionSearchText(option: FieldOption): string {
  return `${option.value} ${option.label}`;
}

function getOptionScore(input: string, option: FieldOption): number {
  const normalizedInput = normalizeText(input);
  const normalizedValue = normalizeText(option.value);
  const normalizedLabel = normalizeText(option.label);
  const normalizedFull = normalizeText(optionSearchText(option));
  const inputDigits = onlyDigits(input);
  const optionDigits = onlyDigits(optionSearchText(option));

  if (!normalizedInput && !inputDigits) return 0;

  if (inputDigits.length >= 6 && optionDigits.includes(inputDigits)) return 120;
  if (normalizedInput === normalizedValue || normalizedInput === normalizedLabel || normalizedInput === normalizedFull) return 110;
  if (normalizedValue.includes(normalizedInput) || normalizedLabel.includes(normalizedInput)) return 95;
  if (normalizedInput.includes(normalizedValue) || normalizedInput.includes(normalizedLabel)) return 90;

  const inputWords = normalizedInput.split(' ').filter((word) => word.length >= 2);
  const fullWords = new Set(normalizedFull.split(' ').filter(Boolean));
  const matchedWords = inputWords.filter((word) => fullWords.has(word) || normalizedFull.includes(word));

  if (inputWords.length > 0 && matchedWords.length === inputWords.length) return 80;
  if (inputWords.length > 0 && matchedWords.length / inputWords.length >= 0.6) return 65;

  return 0;
}

function findBestOption(input: string, options: FieldOption[], excluded: Set<string>): FieldOption | null {
  let best: FieldOption | null = null;
  let bestScore = 0;

  options.forEach((option) => {
    if (excluded.has(option.value)) return;
    const score = getOptionScore(input, option);
    if (score > bestScore) {
      best = option;
      bestScore = score;
    }
  });

  return bestScore >= 60 ? best : null;
}

function valuesToArray(value: string | string[]): string[] {
  if (Array.isArray(value)) return value;
  return splitValues(value);
}

function buildTextValue(currentValue: string | string[], imported: string[]): string {
  return unique([...valuesToArray(currentValue), ...imported]).join(',');
}

function buildOptionsValue(
  currentValue: string | string[],
  imported: string[],
  options: FieldOption[],
  excludedValues: string[]
): { value: string[]; matched: number; notMatched: string[] } {
  const excluded = new Set(excludedValues);
  const current = valuesToArray(currentValue).filter((value) => !excluded.has(value));
  const matchedValues: string[] = [];
  const notMatched: string[] = [];

  imported.forEach((input) => {
    const option = findBestOption(input, options, excluded);
    if (option) matchedValues.push(option.value);
    else notMatched.push(input);
  });

  return {
    value: unique([...current, ...matchedValues]),
    matched: unique(matchedValues).length,
    notMatched,
  };
}

export function buildBulkImportValue({
  mode,
  options = [],
  currentValue,
  excludedValues = [],
  rawValues,
}: BulkImportValueParams): BulkImportValueResult {
  const imported = unique(rawValues.flatMap(splitValues));

  if (imported.length === 0) {
    return {
      applied: false,
      message: 'Nenhum valor encontrado para importar.',
    };
  }

  if (mode === 'text') {
    return {
      applied: true,
      value: buildTextValue(currentValue, imported),
      message: `${imported.length} valor(es) adicionados.`,
    };
  }

  const result = buildOptionsValue(currentValue, imported, options, excludedValues);

  if (mode === 'singleOption') {
    const firstValue = result.value[result.value.length - 1] ?? '';

    return {
      applied: true,
      value: firstValue,
      message: firstValue
        ? `Selecionado automaticamente. ${result.notMatched.length ? `${result.notMatched.length} valor(es) não encontrados.` : ''}`
        : 'Nenhuma opção correspondente encontrada.',
    };
  }

  return {
    applied: true,
    value: result.value,
    message: `${result.matched} item(ns) selecionado(s).${result.notMatched.length ? ` ${result.notMatched.length} não encontrado(s).` : ''}`,
  };
}

async function extractSpreadsheetValues(file: File): Promise<string[]> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const values: string[] = [];

  workbook.SheetNames.forEach((sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const rangeRef = sheet['!ref'];
    if (!rangeRef) return;

    const range = XLSX.utils.decode_range(rangeRef);

    for (let row = range.s.r; row <= range.e.r; row += 1) {
      for (let column = range.s.c; column <= range.e.c; column += 1) {
        const address = XLSX.utils.encode_cell({ r: row, c: column });
        const cell = sheet[address] as SpreadsheetCell | undefined;
        if (!cell) continue;

        const value = spreadsheetCellToText(cell).trim();
        if (value) values.push(value);
      }
    }
  });

  return values;
}

async function extractFileValues(file: File): Promise<string[]> {
  const lowerName = file.name.toLowerCase();
  if (lowerName.endsWith('.csv') || lowerName.endsWith('.txt')) {
    return splitValues(await file.text());
  }

  return extractSpreadsheetValues(file);
}

export default function BulkImportActions({
  mode,
  options = [],
  currentValue,
  onApply,
  excludedValues = [],
  buttonLabel = 'Importar lista',
}: BulkImportActionsProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = useState<string>('');

  const applyImportedValues = (rawValues: string[]) => {
    const result = buildBulkImportValue({
      mode,
      options,
      currentValue,
      excludedValues,
      rawValues,
    });

    if (result.applied && result.value !== undefined) {
      onApply(result.value);
    }

    setMessage(result.message);
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const values = await extractFileValues(file);
      applyImportedValues(values);
    } catch (error) {
      console.error(error);
      setMessage('Não foi possível ler o arquivo. Verifique se é .xls, .xlsx, .csv ou .txt.');
    }
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      applyImportedValues(parseBulkImportText(text));
    } catch (error) {
      console.error(error);
      setMessage('Não foi possível ler a área de transferência. Cole em um arquivo ou libere a permissão do navegador.');
    }
  };

  return (
    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <input
          ref={inputRef}
          hidden
          type="file"
          accept=".xls,.xlsx,.csv,.txt"
          onChange={handleFileChange}
        />
        <Button
          size="small"
          variant="outlined"
          startIcon={<UploadFileOutlinedIcon />}
          onClick={() => inputRef.current?.click()}
        >
          {buttonLabel}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentPasteOutlinedIcon />}
          onClick={handlePasteFromClipboard}
        >
          Colar lista
        </Button>
      </Stack>
      {message && <FormHelperText>{message}</FormHelperText>}
    </Stack>
  );
}
